// src/utils/AudioManager.js

import { useSoundsStore } from "../store/useSoundsStore";

class AudioManager {

  static audioCtx = new (window.AudioContext || window.webkitAudioContext)();

  // The currently playing background audio
  static bgAudio = null;
  static bgSource = null;
  static gainNode = null;


  static playlist = null; // { urls: string[], idx: number, volume: number }

  // A lookup table for preloaded SFX Audio instances
  static sfxBank = {};

  // static pendingMusic = null;
  static pendingPlaylist = null;

  /**
   * Preload a sound effect into the sfxBank.
   * @param {string} key    Unique identifier for this SFX
   * @param {string} url    URL or import path to the audio file
   */
  static async loadSFX(key, url) {
    const audio = new Audio(url);
    // Begin loading immediately
    audio.preload = "auto";
    // We don’t play yet; just cache it
    this.sfxBank[key] = audio;
  }

  /**
   * Play a preloaded SFX. Clones the Audio node so multiple can overlap.
   * @param {string} key
   * @param {number} [volume=0.5]
   */
  static playSFX(key, volumeOverride = null) {
    const original = this.sfxBank[key];
    if (!original) {
      console.warn(`AudioManager: no SFX loaded for key "${key}"`);
      return;
    }

    // Get volume from store unless manually overridden
    const volume =
      volumeOverride !== null
        ? volumeOverride
        : useSoundsStore.getState().sfxVolume;

    const clip = original.cloneNode();
    clip.volume = volume;
    clip.play().catch((err) => {
      console.error("AudioManager.playSFX() failed:", err);
    });
  }

  /**
   * Start playing a looping background track.
   * Stops any already‐playing music.
   * @param {string} url
   * @param {number} [volume=0.5]
   */


  // static async playMusic(url, volume = 0.5) {

  //   // 3.1 Stop + tear down any existing tracks
  //   if (this.bgAudio) {
  //     this.bgAudio.pause();
  //     this.bgAudio = null;
  //     if (this.bgSource) this.bgSource.disconnect();
  //     if (this.gainNode) this.gainNode.disconnect();
  //     this.bgSource = this.gainNode = null;
  //   }

  //   // 3.2 Create a fresh HTMLAudioElement
  //   const music = new Audio(url);
  //   music.loop = true;
  //   // music.volume = volume;

  //   // 3.3 Hook it into Web-Audio via MediaElementSource → GainNode
  //   const src = this.audioCtx.createMediaElementSource(music);
  //   const gain = this.audioCtx.createGain();
  //   gain.gain.value = volume;
  //   src.connect(gain).connect(this.audioCtx.destination);

  //   // 3.4 Save references so we can stop/change later
  //   this.bgAudio = music;
  //   this.bgSource = src;
  //   this.gainNode = gain;

  //   // 3.5 On iOS, you must resume() the context on a user gesture
  //   await this.audioCtx.resume();
  //   music.play().catch((err) =>
  //     console.error("AudioManager.playMusic() failed:", err)
  //   );


  // }

  // static async playMusic(url, volume = 0.5) {
  //   // 🧠 Save the music we tried to play, in case it fails
  //   // this.pendingMusic = { url, volume };

  //   try {
  //     // 📣 Important: resume the Web Audio API context (may fail on some browsers until interaction)
  //     await this.audioCtx.resume();

  //     // 🔇 Stop and clean up any currently playing music
  //     if (this.bgAudio) {
  //       this.bgAudio.pause();
  //       this.bgAudio = null;
  //     }
  //     if (this.bgSource) this.bgSource.disconnect();
  //     if (this.gainNode) this.gainNode.disconnect();
  //     this.bgSource = this.gainNode = null;

  //     // 🎵 Create a new <audio> element and wire it up through Web Audio
  //     const music = new Audio(url);
  //     music.loop = true;

  //     const src = this.audioCtx.createMediaElementSource(music);
  //     const gain = this.audioCtx.createGain();
  //     gain.gain.value = volume;
  //     src.connect(gain).connect(this.audioCtx.destination);

  //     // 💾 Save references so we can stop/change later
  //     this.bgAudio = music;
  //     this.bgSource = src;
  //     this.gainNode = gain;

  //     // ▶️ Try to play the music!
  //     await music.play();

  //     // ✅ If successful, clear the pending track
  //     // this.pendingMusic = null;
  //   } catch (err) {
  //     console.error("AudioManager.playMusic() failed:", err);
  //     // ❗ Leave `pendingMusic` set so we can retry it later after user interaction
  //   }
  // }

  /**
   * Pause the currently playing background track.
   */
  // static stopMusic() {
  //   if (this.bgAudio) {
  //     this.bgAudio.pause();
  //     this.bgAudio = null;
  //   }
  // }

  static stopMusic() {
    if (this.bgAudio) {
      this.bgAudio.pause();
      this.bgAudio.src = ""; // 🚨 stops loading further data
      this.bgAudio.remove(); // 🚨 fully remove the audio element
      this.bgAudio = null;
    }
    this.bgSource = null;
    this.gainNode = null;
    this.playlist = null;
    this.pendingPlaylist = null;
  }


  /**
   * Start a looping playlist of background tracks.
   * URLs is an array of strings; we'll step through them in order.
   */
  static playPlaylist(urls, volume = 0.5) {
    if (!urls?.length) return;

    // 🧠 Check if bgAudio is already playing this exact track
    const samePlaylist =
      this.playlist &&
      JSON.stringify(this.playlist.urls) === JSON.stringify(urls);

    const isPlaying =
      this.bgAudio && !this.bgAudio.paused && !this.bgAudio.ended;

    if (samePlaylist && isPlaying) {
      console.log("🎵 Playlist already playing. Skip.");
      return;
    }

    // tear down anything already playing
    this.stopMusic();

    // Save playlist intent in case autoplay fails
    this.pendingPlaylist = { urls, volume, idx: 0 };

    if (this.bgAudio) {
      this.bgAudio.pause();
      this.bgAudio = null;
    }

    // set up the new playlist
    this.playlist = { urls, idx: 0, volume };

    // start the first track
    this._playNext();
  }

  // internal helper: actually instantiate and play the current track
  static async _playNext() {
    if (!this.playlist) return;

    const { urls, idx, volume } = this.playlist;
    const url = urls[idx];

    // create a fresh <audio>
    const audio = new Audio(url);
    audio.loop = false; // we’ll handle looping at playlist level
    audio.volume = this.playlist.volume;

    // when this track ends, bump the index and play again
    audio.onended = () => {
      // wrap the index
      this.playlist.idx = (idx + 1) % urls.length;
      // play the next
      this._playNext();
    };

    // save & play
    this.bgAudio = audio;


    try {
      // 🧠 Important: resume audio context first (like playMusic)
      await this.audioCtx.resume();

      await audio.play();
      this.pendingPlaylist = null;
    } catch (err) {
      console.error("AudioManager._playNext() failed:", err);
      // Keep pendingPlaylist in place for retry
    }
    // audio.play().catch((err) => {
    //   console.error("AudioManager._playNext() failed:", err);
    // });

  }

  /**
   * Adjust the volume of the current background track on the fly.
   * @param {number} volume
   */
  static setMusicVolume(volume) {
    // if (this.bgAudio) {
    //   this.bgAudio.volume = volume;
    // }
    // Web-Audio path (playMusic)
    if (this.gainNode) {
      this.gainNode.gain.value = volume;
    }
    // HTMLAudioElement fallback (playPlaylist)
    if (this.bgAudio) {
      this.bgAudio.volume = volume;
    }
    // If you also want new playlist entries to pick up the new volume:
    if (this.playlist) {
      this.playlist.volume = volume;
    }
  }
}

export default AudioManager;
